import { useState, useMemo, useEffect, useId } from 'react';
import { AlertTriangle, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { getHistorialVentas, encolarAnularVenta, type VentaHistorialItem } from '@/lib/sync-queue';
import { formatMoney } from '@/lib/format';
import { formatDateTime } from '@mibodega/shared-types';
import { Dialog } from './Dialog';
import { Button } from './Button';

interface Props {
  open: boolean;
  onClose: () => void;
  onAnulada?: (ventaId: string) => void;
  organizationId: string;
}

const MOTIVOS_RAPIDOS = [
  'Error del cajero',
  'Cliente cambió de opinión',
  'Producto dañado',
  'Pago rechazado',
  'Duplicada',
];

export function AnularVentaModal({ open, onClose, onAnulada, organizationId }: Props) {
  const titleId = useId();
  const descId = useId();
  const filterId = useId();
  const motivoId = useId();

  const [ventas, setVentas] = useState<VentaHistorialItem[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [motivo, setMotivo] = useState('');
  const [filter, setFilter] = useState('');

  const motivoError = motivo.length > 0 && motivo.trim().length < 3 ? 'Mínimo 3 caracteres' : null;

  useEffect(() => {
    if (!open) return;
    let cancelado = false;
    setVentas([]);
    setSelectedId('');
    setMotivo('');
    setFilter('');
    void getHistorialVentas()
      .then((items) => {
        if (!cancelado) setVentas(items);
      })
      .catch((err) => {
        if (!cancelado) {
          toast.error(err instanceof Error ? err.message : String(err));
        }
      });
    return () => {
      cancelado = true;
    };
  }, [open]);

  const visibleVentas = useMemo(() => {
    if (!filter) return ventas;
    const q = filter.toLowerCase();
    return ventas.filter(
      (v) => v.codigo.toLowerCase().includes(q) || v.id.toLowerCase().includes(q),
    );
  }, [ventas, filter]);

  const selected = useMemo(
    () => ventas.find((v) => v.id === selectedId) ?? null,
    [ventas, selectedId],
  );

  async function handleAnular() {
    if (!selected) {
      toast.error('Selecciona una venta');
      return;
    }
    if (motivo.trim().length < 3) {
      toast.error('El motivo debe tener al menos 3 caracteres');
      return;
    }
    if (selected.anulada) {
      toast.error('Esta venta ya fue anulada');
      return;
    }

    // Un fallo de escritura en IndexedDB sin try/catch dejaba al cajero tocando
    // "Anular venta" sin que pasara absolutamente nada: ni toast ni cambio en
    // el diálogo.
    let encolada: boolean;
    try {
      encolada = await encolarAnularVenta({
        organizationId,
        ventaId: selected.id,
        motivo: motivo.trim(),
      });
    } catch (err) {
      toast.error(
        `No se pudo encolar la anulación de ${selected.codigo}: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
      return;
    }

    if (encolada) {
      toast.success(
        `Venta ${selected.codigo} marcada para anular. Se aplicará cuando haya internet.`,
        { duration: 4000 },
      );
    } else {
      // No se encoló porque ya había una op en vuelo para esta venta: afirmar
      // éxito acá le mentiría al cajero.
      toast.info(
        `La anulación de ${selected.codigo} ya estaba en cola. Se aplicará al sincronizar.`,
      );
    }

    if (onAnulada) onAnulada(selected.id);
    onClose();
  }

  const canSubmit = !!selected && motivo.trim().length >= 3;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <Ban className="h-5 w-5" aria-hidden="true" />
          Anular venta
        </span>
      }
      titleId={titleId}
      descriptionId={descId}
      description="Selecciona una venta sincronizada recientemente e ingresa el motivo. La anulación se aplicará cuando vuelvas a tener internet."
      size="lg"
      destructive
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              void handleAnular();
            }}
            disabled={!canSubmit}
            leadingIcon={<Ban className="h-4 w-4" aria-hidden="true" />}
          >
            Anular venta
          </Button>
        </>
      }
    >
      {ventas.length === 0 ? (
        <div className="rounded-md border border-dashed border-neutral-200 bg-neutral-50 px-4 py-8 text-center text-sm text-neutral-500">
          <AlertTriangle className="text-warning mx-auto mb-2 h-6 w-6" aria-hidden="true" />
          No hay ventas sincronizadas en este dispositivo todavía.
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label htmlFor={filterId} className="sr-only">
              Buscar venta por código
            </label>
            <input
              id={filterId}
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Buscar por código o UUID..."
              className="bg-neutral-0 w-full rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
            />
          </div>

          <div
            className="max-h-64 overflow-y-auto rounded-md border border-neutral-200"
            role="listbox"
            aria-label="Ventas disponibles para anular"
          >
            {visibleVentas.length === 0 ? (
              <p className="p-4 text-center text-sm text-neutral-500">Sin resultados.</p>
            ) : (
              <ul className="divide-y divide-neutral-200">
                {visibleVentas.map((v) => {
                  const isSelected = selectedId === v.id;
                  return (
                    <li key={v.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        aria-disabled={v.anulada}
                        onClick={() => !v.anulada && setSelectedId(v.id)}
                        disabled={v.anulada}
                        className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus-ring)] disabled:cursor-not-allowed disabled:opacity-60 ${
                          isSelected ? 'bg-danger-soft' : 'hover:bg-neutral-100'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            aria-hidden="true"
                            className={`rounded-pill flex h-4 w-4 items-center justify-center border ${
                              isSelected
                                ? 'border-danger bg-danger text-neutral-0'
                                : 'bg-neutral-0 border-neutral-300'
                            }`}
                          >
                            {isSelected && (
                              <svg
                                width="10"
                                height="10"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="3"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                            )}
                          </span>
                          <div>
                            <div className="font-mono font-medium text-neutral-900">{v.codigo}</div>
                            <div className="text-xs text-neutral-500">
                              {formatDateTime(v.fecha)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-neutral-900">
                            {formatMoney(v.total, v.monedaCodigo)}
                          </span>
                          {v.anulada && (
                            <span className="rounded-pill bg-danger-soft text-danger px-2 py-0.5 text-xs font-medium">
                              Anulada
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div>
            <label htmlFor={motivoId} className="mb-1 block text-sm font-medium text-neutral-700">
              Motivo <span className="text-danger">*</span>
            </label>
            <input
              id={motivoId}
              type="text"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Mínimo 3 caracteres"
              aria-invalid={!!motivoError}
              aria-describedby={motivoError ? `${motivoId}-err` : undefined}
              className="bg-neutral-0 aria-[invalid=true]:border-danger w-full rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
            />
            {motivoError && (
              <p id={`${motivoId}-err`} className="text-danger mt-1 text-xs">
                {motivoError}
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Motivos rápidos">
              {MOTIVOS_RAPIDOS.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMotivo(m)}
                  className="rounded-pill border border-neutral-200 bg-neutral-100 px-3 py-1 text-xs text-neutral-700 transition-colors hover:bg-neutral-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)]"
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          {selected && (
            <div
              role="alert"
              className="border-danger bg-danger-soft text-danger rounded-md border px-3 py-2 text-xs"
            >
              Vas a anular <strong>{selected.codigo}</strong> por{' '}
              <strong>{formatMoney(selected.total, selected.monedaCodigo)}</strong>. Esta acción
              revierte stock y movimientos de caja. No se puede deshacer.
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
